// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { BlockLoader } from '../components/loading';
import FilterDropdownButton from '../components/FilterDropdownButton';
import MyApprovalsHeader from '../components/MyApprovalsHeader';
import { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  Search,
  X,
  Check,
  Loader2,
  Download,
} from 'lucide-react';
import { approvalsAPI, buildLeaveDocumentUrl } from '../services/api';
import { downloadCsvLegacy } from '../utils/download';
import { useAuthStore } from '../stores/authStore';
import { useLinkedSearch } from '../hooks/useLinkedSearch';
import { useNavigate, useSearchParams } from 'react-router-dom';

type RequestStatus = 'pending' | 'approved' | 'rejected';
type StatusFilter = RequestStatus | 'all';

interface LeaveRequest {
  id: string;
  type: 'leave';
  requestId: number;
  employeeName: string;
  employeeCode: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: RequestStatus;
  appliedOn: string;
  approvalAuthority?: string | null;
  // Supporting-document S3 key (present only when the employee uploaded one).
  documentUrl?: string | null;
}

interface RegularizationRequest {
  id: string;
  type: 'regularization';
  requestId: number;
  employeeName: string;
  employeeCode: string;
  date: string;
  checkIn: string;
  checkOut: string;
  reason: string;
  status: RequestStatus;
  isMarketRegularization: boolean;
  leaveType?: string | null;
  leaveDays?: number | null;
  appliedOn: string;
  approvalAuthority?: string | null;
}

/**
 * A comp-off CLAIM (earning a day), not a comp-off leave application (spending
 * one). `requestId` is the 1Office attendance row id, and `date` is the holiday
 * the employee worked — `appliedOn` is when they filed the claim.
 */
interface CompOffRequest {
  id: string;
  type: 'compoff';
  requestId: number;
  employeeName: string;
  employeeCode: string;
  date: string;
  /** 1 = full day, 0.5 = half day. */
  days: number;
  reason: string;
  status: RequestStatus;
  appliedOn: string;
  approvalAuthority?: string | null;
  /** Always 'Comp Off' — lets the leave-type filter surface claims. */
  leaveType?: string | null;
}

type ApprovalRequest = LeaveRequest | RegularizationRequest | CompOffRequest;

function TruncatedReason({ reason }: { reason: string }) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [reason]);

  return (
    <div className="relative inline-block group max-w-[180px]">
      <p ref={ref} className="text-sm text-gray-700 truncate">{reason}</p>
      {isTruncated && (
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 hidden group-hover:block">
          <div className="bg-gray-900 text-white text-xs rounded-md shadow-lg px-3 py-2 whitespace-normal w-64 text-left">
            <div className="text-gray-400 mb-1">Reason</div>
            <div className="break-words">{reason}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const statusConfig = {
  pending: {
    label: 'Pending',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    icon: Clock,
  },
  approved: {
    label: 'Approved',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    icon: CheckCircle,
  },
  rejected: {
    label: 'Rejected',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    icon: XCircle,
  },
};

// Two things this page shows are named in its address, so that a link — the home
// screen's "approvals waiting" row above all — opens exactly the list it counted:
//
//   ?status=pending | approved | rejected   the Status filter. Missing or unknown: all.
//   ?dates=all                              no Requested-date limit of the page's own
//                                           (both inputs empty).
//   ?dates=<from>..<to>                     the dates the person picked, as YYYY-MM-DD;
//                                           either side may be empty, not both.
//   no ?dates, or an unknown value          this month so far, the page's own default.
//
// `dates=all` is the one a link needs: a count of everything still pending includes
// requests raised before the 1st, which the default window hides. The <from>..<to> form
// exists so the address stays the single place that says which dates are on screen once
// the person changes them — without it the inputs would need a second copy in state.
//
// None of these reaches further back than the server reads: see READS_BACK_LINE.

// How far back the list goes, whatever the two date boxes say. The server never reads
// more (getAllApprovalRequests in approvals.controller: leave and attendance requests
// from the last 60 days, comp-off claims from the last 90). An empty From box reads as
// "since the beginning", so the page says this whenever the Requested dates have no start.
const READS_BACK_LINE = 'Showing requests from the last 60 days. Comp-off claims go back 90.';

const statusFromAddress = (value: string | null): StatusFilter =>
  value === 'pending' || value === 'approved' || value === 'rejected' ? value : 'all';

// A real calendar day written YYYY-MM-DD, or '' for an open end. Anything else is not a
// value this page wrote, so the whole ?dates is ignored rather than half-applied.
const isDayOrEmpty = (day: string): boolean => {
  if (!day) return true;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return false;
  const [year, month, date] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Day 0 of the next month is the last day of this one. Set with setFullYear, because
  // the Date constructor reads a year below 100 as 19xx — and a date box passes through
  // years like 0002 while a year is being typed, which must not be thrown away.
  const lastOfMonth = new Date(2000, 0, 1);
  lastOfMonth.setFullYear(year, month, 0);
  return month >= 1 && month <= 12 && date >= 1 && date <= lastOfMonth.getDate();
};

// Both boxes empty is "no date limit", which must be said out loud: a missing value
// would mean this month again.
const datesForAddress = (from: string, to: string): string => (from || to ? `${from}..${to}` : 'all');

const datesFromAddress = (
  value: string | null,
  fallback: { from: string; to: string },
): { from: string; to: string } => {
  if (value === 'all') return { from: '', to: '' };
  const parts = (value ?? '').split('..');
  if (parts.length !== 2) return fallback;
  const [from, to] = parts;
  if ((!from && !to) || !isDayOrEmpty(from) || !isDayOrEmpty(to)) return fallback;
  return { from, to };
};

export default function MyApprovalsPage() {
  const navigate = useNavigate();
  // Above the access check below, so the hook runs on every render.
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, clearAuth, selectedProjectId } = useAuthStore();
  const currentUserId = user?.userId || 0;
  const currentUserIdRef = useRef(currentUserId);
  const hasFetchedRef = useRef(false);

  currentUserIdRef.current = currentUserId;

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  if (!currentUserId) {
    return (
      <AccessDeniedPanel
        message="You don't have permission to access the approvals page. Please contact your administrator if you believe this is an error."
        onLogout={handleLogout}
      />
    );
  }

  // Default the Requested-Date range to the current month so the page opens
  // scoped to "this month" rather than dumping every request in range.
  const monthDefaults = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    return { first, today };
  }, []);

  // The Status filter and the Requested dates are read from the address on every render,
  // never copied into state, so a link, a refresh and Back all agree on what is listed.
  const statusFilter = statusFromAddress(searchParams.get('status'));
  const datesInAddress = searchParams.get('dates');
  const { from: appliedFromDate, to: appliedToDate } = useMemo(
    () => datesFromAddress(datesInAddress, { from: monthDefaults.first, to: monthDefaults.today }),
    [datesInAddress, monthDefaults],
  );

  // Both writers replace rather than add a step, so Back leaves the page instead of
  // undoing one filter at a time, and both keep every other value in the query.
  const setStatusFilter = (next: StatusFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      // "All" is the page's default and has no value of its own in the address.
      if (next === 'all') params.delete('status');
      else params.set('status', next);
      return params;
    }, { replace: true });
  };
  // What the person has just put into the two date boxes, shown there at once and held
  // only until the address shows it too (`as` is the ?dates written for it). The router
  // applies an address change a moment later, as a transition, and a date box that waits
  // for it is put back to its old value between keystrokes — which loses the year being
  // typed. The LIST never reads this: what is listed follows the address alone.
  const [typedDates, setTypedDates] = useState<{ from: string; to: string; as: string } | null>(null);
  // Let go the moment the address has caught up (and when the person leaves the box,
  // see onBlur), so an address that changes later — a link, Back — is never hidden behind
  // something typed earlier.
  useEffect(() => {
    if (typedDates && typedDates.as === datesInAddress) setTypedDates(null);
  }, [typedDates, datesInAddress]);
  const shownDates = typedDates ?? { from: appliedFromDate, to: appliedToDate };

  const setAppliedDates = (from: string, to: string) => {
    const as = datesForAddress(from, to);
    setTypedDates({ from, to, as });
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('dates', as);
      return params;
    }, { replace: true });
  };

  // A link can open the list narrowed to one person (?employeeCode=). It should carry
  // dates=all with it: the default window is this month, and the person's requests
  // may be older.
  const [searchQuery, setSearchQuery] = useLinkedSearch();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>('all');
  const [showLeaveTypeDropdown, setShowLeaveTypeDropdown] = useState(false);
  const [authorityFilter, setAuthorityFilter] = useState<string>('all');
  const [showAuthorityDropdown, setShowAuthorityDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rejectModal, setRejectModal] = useState<{ requestId: string; requestType: ApprovalRequest['type']; employeeName: string; numericId: number; requestedDate: string } | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [regularizationRequests, setRegularizationRequests] = useState<RegularizationRequest[]>([]);
  const [compOffRequests, setCompOffRequests] = useState<CompOffRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchApprovalRequests = useCallback(async () => {
    const userId = currentUserIdRef.current;
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await approvalsAPI.getAllApprovalRequests(userId, {
        projectId: selectedProjectId ?? undefined,
        employeeCode: user?.userCode?.toString() || user?.userName || undefined,
      });
      
      if (response.data.success) {
        setLeaveRequests(response.data.data?.leaveRequests || []);
        setRegularizationRequests(response.data.data?.regularizationRequests || []);
        setCompOffRequests(response.data.data?.compOffRequests || []);
      }
    } catch (err) {
      console.error('Error fetching approval requests:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!hasFetchedRef.current && currentUserId) {
      hasFetchedRef.current = true;
      fetchApprovalRequests();
    }
  }, [currentUserId, fetchApprovalRequests]);

  // Re-fetch when selected project changes
  useEffect(() => {
    if (hasFetchedRef.current && currentUserId) {
      fetchApprovalRequests();
    }
  }, [selectedProjectId, fetchApprovalRequests]);

  // Combine all requests into single list
  const allRequests: ApprovalRequest[] = [...leaveRequests, ...regularizationRequests, ...compOffRequests];

  // Distinct leave types present in the loaded data — drives the Leave Type
  // filter dropdown so it only offers types that actually exist.
  const leaveTypeOptions = useMemo(() => {
    const set = new Set<string>();
    leaveRequests.forEach((r) => { if (r.leaveType) set.add(r.leaveType); });
    regularizationRequests.forEach((r) => { if (r.leaveType) set.add(r.leaveType); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leaveRequests, regularizationRequests]);

  // If the selected leave type is no longer present after a refetch, reset to
  // "all" so the list doesn't get stuck showing nothing.
  useEffect(() => {
    if (leaveTypeFilter !== 'all' && !leaveTypeOptions.includes(leaveTypeFilter)) {
      setLeaveTypeFilter('all');
    }
  }, [leaveTypeOptions, leaveTypeFilter]);

  // Distinct approval authorities (reporting managers) present in the data —
  // drives the Approval Authority filter dropdown.
  const authorityOptions = useMemo(() => {
    const set = new Set<string>();
    allRequests.forEach((r) => { if (r.approvalAuthority) set.add(r.approvalAuthority); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allRequests]);

  useEffect(() => {
    if (authorityFilter !== 'all' && !authorityOptions.includes(authorityFilter)) {
      setAuthorityFilter('all');
    }
  }, [authorityOptions, authorityFilter]);

  const filteredRequests = allRequests.filter((request) => {
    // Filter by status
    if (statusFilter !== 'all' && request.status !== statusFilter) return false;

    // Filter by leave type — matches any request carrying the selected leave
    // type (leave requests always have one; regularizations may carry one).
    if (leaveTypeFilter !== 'all' && request.leaveType !== leaveTypeFilter) return false;

    // Filter by Approval Authority (reporting manager)
    if (authorityFilter !== 'all' && request.approvalAuthority !== authorityFilter) return false;

    // Filter by search — Employee Name or Employee Code only
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        request.employeeName.toLowerCase().includes(query) ||
        request.employeeCode.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Filter by Applied Date range (inclusive on both ends)
    if (appliedFromDate || appliedToDate) {
      const applied = request.appliedOn ? new Date(request.appliedOn) : null;
      if (!applied || isNaN(applied.getTime())) return false;
      // Compare date-only (ignore time of day) so the boundary days are inclusive
      const appliedDay = new Date(applied.getFullYear(), applied.getMonth(), applied.getDate());
      if (appliedFromDate) {
        const from = new Date(appliedFromDate);
        const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
        if (appliedDay < fromDay) return false;
      }
      if (appliedToDate) {
        const to = new Date(appliedToDate);
        const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
        if (appliedDay > toDay) return false;
      }
    }

    return true;
  });

  // Sort by applied date (most recent first)
  const sortedRequests = filteredRequests.sort(
    (a, b) => new Date(b.appliedOn).getTime() - new Date(a.appliedOn).getTime()
  );

  // Header counts reflect the *filtered* set so they react to the active filters.
  const filteredPendingCount = filteredRequests.filter((r) => r.status === 'pending').length;

  const pageSubtitle =
    `${filteredRequests.length} request${filteredRequests.length !== 1 ? 's' : ''}` +
    ` • ${filteredPendingCount} pending awaiting your action`;

  // Client-side pagination over the filtered+sorted set.
  const totalRecords = sortedRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedRequests = sortedRequests.slice(pageStart, pageStart + pageSize);

  // Reset to the first page whenever the filtered result set changes shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, leaveTypeFilter, authorityFilter, searchQuery, appliedFromDate, appliedToDate, pageSize]);

  const handleExportCSV = () => {
    if (!sortedRequests.length) return;
    const headers = [
      'ID', 'Type', 'Employee Name', 'Employee Code', 'Approval Authority', 'Applied On',
      'Start Date', 'End Date', 'Days', 'Leave Type', 'Check In', 'Check Out',
      'Reason', 'Status',
    ];
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = sortedRequests.map((r) => {
      // Narrowed per branch rather than by an isLeave flag: with three request
      // types a boolean no longer tells the compiler (or the reader) which
      // fields exist, and a comp-off row has neither check-in/out nor a range.
      const isLeave = r.type === 'leave';
      const isReg = r.type === 'regularization';
      return [
        r.id,
        r.type,
        r.employeeName,
        r.employeeCode,
        r.approvalAuthority || '',
        r.appliedOn,
        isLeave ? r.startDate : r.date,
        isLeave ? r.endDate : r.date,
        isLeave || r.type === 'compoff' ? r.days : '',
        // Kept as `isLeave ? … : ''` for the two existing types so the exported
        // column is byte-identical to today; comp off is additive.
        isLeave || r.type === 'compoff' ? (r.leaveType ?? '') : '',
        isReg ? r.checkIn : '',
        isReg ? r.checkOut : '',
        r.reason,
        r.status,
      ].map(escape).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    downloadCsvLegacy(`my-approvals-${new Date().toISOString().split('T')[0]}.csv`, csv);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // One updater per request type. A map rather than a ternary chain so adding a
  // type can't silently inherit another type's endpoint.
  const UPDATE_FNS: Record<ApprovalRequest['type'], typeof approvalsAPI.updateLeaveRequest> = {
    leave: approvalsAPI.updateLeaveRequest,
    regularization: approvalsAPI.updateRegularizationRequest,
    compoff: approvalsAPI.updateCompOffRequest,
  };

  // Surface the server's own message rather than a generic one. It matters most
  // for comp off: approving is what makes 1Office credit the leave balance, and
  // the backend fails the call when its post-write read-back shows no credit
  // happened — a message the manager needs to actually see.
  const decisionErrorMessage = (err: unknown, fallback: string): string => {
    const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
    return data?.message || fallback;
  };

  const handleApprove = async (request: ApprovalRequest) => {
    try {
      setActionLoading(request.id);

      const res = await UPDATE_FNS[request.type](request.requestId, {
        status: 'approved',
        approverId: currentUserId,
      });

      // The decision stands but 1Office attributed it to another user — worth
      // showing, since the audit trail there is now wrong.
      if (res?.data?.warning) alert(res.data.warning);

      await fetchApprovalRequests();
    } catch (err) {
      console.error('Error approving request:', err);
      alert(decisionErrorMessage(err, 'Failed to approve request. Please try again.'));
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (request: ApprovalRequest) => {
    const requestedDate = request.type === 'leave' 
      ? `${request.startDate}${request.endDate !== request.startDate ? ` - ${request.endDate}` : ''}`
      : request.date;
    setRejectModal({ 
      requestId: request.id, 
      requestType: request.type,
      employeeName: request.employeeName,
      numericId: request.requestId,
      requestedDate,
    });
    setRejectComment('');
  };

  const closeRejectModal = () => {
    setRejectModal(null);
    setRejectComment('');
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectComment.trim()) return;
    
    try {
      setActionLoading(rejectModal.requestId);
      
      await UPDATE_FNS[rejectModal.requestType](rejectModal.numericId, {
        status: 'rejected',
        comments: rejectComment,
        approverId: currentUserId,
      });

      closeRejectModal();
      await fetchApprovalRequests();
    } catch (err) {
      console.error('Error rejecting request:', err);
      alert(decisionErrorMessage(err, 'Failed to reject request. Please try again.'));
    } finally {
      setActionLoading(null);
    }
  };

  const renderStatusBadge = (status: RequestStatus) => {
    const config = statusConfig[status];
    const StatusIcon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const renderTypeBadge = (request: ApprovalRequest) => {
    if (request.type === 'leave') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 whitespace-nowrap">
          Leave
        </span>
      );
    }
    // Checked BEFORE the regularization cast below — that cast is unchecked, so
    // a comp-off row reaching it would render as a regularization request.
    if (request.type === 'compoff') {
      const co = request as CompOffRequest;
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">
          Comp Off{co.days === 0.5 ? ' (Half Day)' : ''}
        </span>
      );
    }
    const reg = request as RegularizationRequest;
    const isMarket = reg.isMarketRegularization;

    // Infer request sub-type for attendance regularization
    const hasLeaveType = !!reg.leaveType;
    const ld = reg.leaveDays ?? null;
    let requestSubtype: string | null = null;
    if (!isMarket) {
      if (hasLeaveType && ld != null && ld > 0 && ld < 0.9) {
        requestSubtype = 'Regularize Half Day & Apply Half Day Leave';
      } else if (hasLeaveType) {
        requestSubtype = 'Apply Full Day Leave';
      } else {
        requestSubtype = 'Regularize (Full Day Present)';
      }
    }

    return (
      <div className="relative inline-block group">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
            isMarket ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
          }`}
        >
          {isMarket ? 'Market Regularization' : 'Attendance Regularization'}
        </span>
        {!isMarket && (
          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 hidden group-hover:block">
            <div className="bg-gray-900 text-white text-xs rounded-md shadow-lg px-3 py-2 whitespace-normal w-64 text-left space-y-1">
              {requestSubtype && (
                <div className="text-gray-300">
                  Request: <span className="text-white">{requestSubtype}</span>
                </div>
              )}
              {hasLeaveType && (
                <div className="text-gray-300">
                  Leave Type: <span className="text-white">{reg.leaveType}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActionButtons = (request: ApprovalRequest) => {
    if (request.status !== 'pending') return null;
    const isLoading = actionLoading === request.id;
    return (
      <div className="flex items-center gap-2">
        <div className="relative group">
          <button
            onClick={() => openRejectModal(request)}
            disabled={isLoading}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Reject
          </span>
        </div>
        <div className="relative group">
          <button
            onClick={() => handleApprove(request)}
            disabled={isLoading}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          </button>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Approve
          </span>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Employee Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Employee Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Applied On</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Approval Authority</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700 font-mono">{request.employeeCode}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 text-sm whitespace-nowrap">{request.employeeName}</p>
                  </td>
                  <td className="px-4 py-3">
                    {renderTypeBadge(request)}
                  </td>
                  <td className="px-4 py-3">
                    {request.type === 'leave' ? (
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{(request as LeaveRequest).leaveType}</p>
                        <p className="text-xs text-gray-500">
                          {formatDate((request as LeaveRequest).startDate)} - {formatDate((request as LeaveRequest).endDate)}
                          <span className="ml-1">({(request as LeaveRequest).days} day{(request as LeaveRequest).days > 1 ? 's' : ''})</span>
                        </p>
                        {buildLeaveDocumentUrl((request as LeaveRequest).documentUrl) && (
                          <a
                            href={buildLeaveDocumentUrl((request as LeaveRequest).documentUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            View Document
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{formatDate((request as RegularizationRequest).date)}</p>
                        <p className="text-xs text-gray-500">
                          {(request as RegularizationRequest).checkIn}{(request as RegularizationRequest).checkOut ? ` - ${(request as RegularizationRequest).checkOut}` : ''}
                        </p>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {request.reason ? (
                      <TruncatedReason reason={request.reason} />
                    ) : (
                      <p className="text-sm text-gray-700">-</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{formatDate(request.appliedOn)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {request.status === 'pending'
                      ? renderActionButtons(request)
                      : renderStatusBadge(request.status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700 whitespace-nowrap">{request.approvalAuthority || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {totalRecords > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/40">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-gray-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                {[25, 50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {pageStart + 1}–{Math.min(pageStart + pageSize, totalRecords)} of {totalRecords}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {safePage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header: the title, then Approvals | Probation Confirmation as tabs. */}
      <MyApprovalsHeader subtitle={pageSubtitle} />

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        <div>
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Employee Name or Employee Code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Requested</span>
                <input
                  type="date"
                  value={shownDates.from}
                  onChange={(e) => setAppliedDates(e.target.value, shownDates.to)}
                  onBlur={() => setTypedDates(null)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="date"
                  value={shownDates.to}
                  onChange={(e) => setAppliedDates(shownDates.from, e.target.value)}
                  onBlur={() => setTypedDates(null)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <FilterDropdownButton
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  label={statusFilter === 'all' ? 'All Status' : statusConfig[statusFilter].label}
                />

                {showStatusDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowStatusDropdown(false)}
                    />
                    <div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('pending');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      Pending
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('approved');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      Approved
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('rejected');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      Rejected
                    </button>
                    </div>
                  </>
                )}
              </div>

              {leaveTypeOptions.length > 0 && (
                <div className="relative">
                  <FilterDropdownButton
                    onClick={() => setShowLeaveTypeDropdown(!showLeaveTypeDropdown)}
                    label={leaveTypeFilter === 'all' ? 'All Leave Types' : leaveTypeFilter}
                  />

                  {showLeaveTypeDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowLeaveTypeDropdown(false)}
                      />
                      <div className="absolute left-0 mt-1 w-56 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                        <button
                          onClick={() => {
                            setLeaveTypeFilter('all');
                            setShowLeaveTypeDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${leaveTypeFilter === 'all' ? 'bg-gray-50 font-medium' : ''}`}
                        >
                          All Leave Types
                        </button>
                        {leaveTypeOptions.map((lt) => (
                          <button
                            key={lt}
                            onClick={() => {
                              setLeaveTypeFilter(lt);
                              setShowLeaveTypeDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${leaveTypeFilter === lt ? 'bg-gray-50 font-medium' : ''}`}
                          >
                            {lt}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {authorityOptions.length > 0 && (
                <div className="relative">
                  <FilterDropdownButton
                    onClick={() => setShowAuthorityDropdown(!showAuthorityDropdown)}
                    label={authorityFilter === 'all' ? 'All Approval Authorities' : authorityFilter}
                  />

                  {showAuthorityDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowAuthorityDropdown(false)}
                      />
                      <div className="absolute left-0 mt-1 w-64 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                        <button
                          onClick={() => {
                            setAuthorityFilter('all');
                            setShowAuthorityDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${authorityFilter === 'all' ? 'bg-gray-50 font-medium' : ''}`}
                        >
                          All Approval Authorities
                        </button>
                        {authorityOptions.map((a) => (
                          <button
                            key={a}
                            onClick={() => {
                              setAuthorityFilter(a);
                              setShowAuthorityDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${authorityFilter === a ? 'bg-gray-50 font-medium' : ''}`}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <button
                onClick={handleExportCSV}
                disabled={!sortedRequests.length}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
            {/* Follows the address like the list does, not a date still being typed. */}
            {!appliedFromDate && <p className="mt-2 text-xs text-gray-500">{READS_BACK_LINE}</p>}
          </div>

          {loading ? (
            <BlockLoader label="Loading approval requests..." className="text-center py-12 bg-white rounded-xl border border-gray-100" />
          ) : error ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Error loading data</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchApprovalRequests}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : sortedRequests.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No requests found</h3>
              <p className="text-gray-500">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'There are no pending approvals at the moment'}
              </p>
            </div>
          ) : (
            renderTable()
          )}
        </div>
      </section>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {rejectModal.requestType === 'leave' ? 'Reject Leave Request' : 'Reject Regularization Request'}
              </h3>
              <button
                onClick={closeRejectModal}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400 uppercase tracking-wide">Requested By</span>
                  <span className="text-sm font-medium text-gray-900">{rejectModal.employeeName}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <span className="block text-[10px] text-gray-400 uppercase tracking-wide">Requested Date</span>
                  <span className="text-sm text-gray-600">{rejectModal.requestedDate}</span>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="rejectComment" className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rejectComment"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeRejectModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectComment.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
